import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function CreateUser() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    test_ids: [],
    batch_id: 'none',
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    fetchBatches();
    if (formData.role === 'user') {
      fetchTests();
    }
  }, [formData.role]);

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await api.get('/tests');
      setTests(response.data.filter(t => t.status === 'published'));
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert "none" back to empty string for backend
      const submitData = {
        ...formData,
        batch_id: formData.batch_id === 'none' ? '' : formData.batch_id
      };
      const response = await api.post('/users/create', submitData);
      
      if (response.data.email_sent) {
        toast.success(`${formData.role === 'admin' ? 'Admin' : 'User'} created successfully! Welcome email sent to ${formData.email}`);
      } else {
        toast.warning(`${formData.role === 'admin' ? 'Admin' : 'User'} created successfully! Email delivery failed - please share credentials manually.`);
      }
      
      navigate('/admin/users');
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create user. Please try again.';
      toast.error(errorMessage);
      setLoading(false);
      return;
    }
  };

  const toggleTest = (testId) => {
    const ids = formData.test_ids.includes(testId)
      ? formData.test_ids.filter(id => id !== testId)
      : [...formData.test_ids, testId];
    setFormData({ ...formData, test_ids: ids });
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/users')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Users
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="create-user-title">
          Create {isSuperAdmin ? 'Admin/User' : 'Test User'}
        </h1>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="user-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="user-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value, test_ids: [] })}
                    >
                      <SelectTrigger data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Test User</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                        <SelectItem value="faculty">Faculty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.role === 'user' && (
                  <div className="space-y-2">
                    <Label htmlFor="batch">Batch (Optional)</Label>
                    <Select
                      value={formData.batch_id}
                      onValueChange={(value) => setFormData({ ...formData, batch_id: value })}
                    >
                      <SelectTrigger data-testid="user-batch-select">
                        <SelectValue placeholder="Select batch" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {batches.map((batch) => (
                          <SelectItem key={batch.id} value={batch.id}>
                            {batch.name} {batch.year && `(${batch.year})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="password"
                      type="text"
                      data-testid="user-password-input"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={generatePassword}
                      data-testid="generate-password-button"
                    >
                      Generate
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">Password will be sent to user via email</p>
                </div>
              </div>
            </div>

            {/* Test Assignment Section - More Prominent */}
            {formData.role === 'user' && tests.length > 0 && (
              <div className="space-y-4 border-t pt-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Test Assignment</h3>
                  <p className="text-sm text-gray-600">Select tests to assign to this user (optional)</p>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-2 border rounded-lg p-4 bg-gray-50">
                  {tests.map((test) => (
                    <div key={test.id} className="flex items-center space-x-3 p-3 bg-white rounded-md hover:bg-blue-50 transition-colors" data-testid={`test-option-${test.id}`}>
                      <Checkbox
                        id={test.id}
                        checked={formData.test_ids.includes(test.id)}
                        onCheckedChange={() => toggleTest(test.id)}
                      />
                      <Label htmlFor={test.id} className="flex-1 cursor-pointer">
                        <p className="font-medium text-gray-900">{test.title}</p>
                        <p className="text-sm text-gray-500">{test.question_ids?.length || 0} questions • {test.config?.duration || 0} mins</p>
                      </Label>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900">
                    {formData.test_ids.length} test(s) selected
                  </p>
                </div>
              </div>
            )}

            {formData.role === 'user' && tests.length === 0 && (
              <div className="space-y-4 border-t pt-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    <strong>Note:</strong> No published tests available for assignment. You can assign tests to this user later from the Tests page.
                  </p>
                </div>
              </div>
            )}

            {/* Email Notification Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 border-t pt-6">
              <p className="text-sm text-blue-900">
                <strong>📧 Email Notification:</strong> Login credentials will be sent to <strong>{formData.email || 'the provided email'}</strong>
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 border-t pt-6">
              <Button type="button" variant="outline" onClick={() => navigate('/admin/users')}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="submit-user-button"
              >
                {loading ? 'Creating...' : `Create ${formData.role === 'admin' ? 'Admin' : 'User'}`}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
}

export default CreateUser;